<template>
	<Skeleton :paragraph="{ rows: 15 }" :title="false" :loading="loading">
		<Form :model="formState" :label-col="{ span: 12 }" :wrapper-col="{ span: 12 }" :colon="false">
			<FormItem :label="$t('settings.vrsSettingsForm.trsLabel')" v-bind="validateInfos.label">
				<Select v-model:value="formState.label" :options="trsOptionsRef" @change="onLabelChange" :getPopupContainer="getPopupContainer" />
			</FormItem>

			<FormItem :label="$t('settings.vrsSettingsForm.trsT')" v-bind="validateInfos.t1">
				<Row>
					<Col :span="8">
						<InputDecimal v-model="formState.t1" :disabled="!paramsEnabled" />
					</Col>
					<Col :span="8">
						<InputDecimal v-model="formState.t2" :disabled="!paramsEnabled" />
					</Col>
					<Col :span="8">
						<InputDecimal v-model="formState.t3" :disabled="!paramsEnabled" />
					</Col>
				</Row>
			</FormItem>

			<FormItem :label="$t('settings.vrsSettingsForm.trsScale')" v-bind="validateInfos.scale">
				<InputDecimal v-model="formState.scale" :disabled="!paramsEnabled" />
			</FormItem>

			<FormItem :label="$t('settings.vrsSettingsForm.trsR')" v-bind="validateInfos.r1">
				<Row>
					<Col :span="8">
						<InputDecimal v-model="formState.r1" :disabled="!paramsEnabled" />
					</Col>
					<Col :span="8">
						<InputDecimal v-model="formState.r2" :disabled="!paramsEnabled" />
					</Col>
					<Col :span="8">
						<InputDecimal v-model="formState.r3" :disabled="!paramsEnabled" />
					</Col>
				</Row>
			</FormItem>

			<FormItem :label="$t('settings.vrsSettingsForm.trsDT')" v-bind="validateInfos.dt1">
				<Row>
					<Col :span="8">
						<InputDecimal v-model="formState.dt1" :disabled="!paramsEnabled" />
					</Col>
					<Col :span="8">
						<InputDecimal v-model="formState.dt2" :disabled="!paramsEnabled" />
					</Col>
					<Col :span="8">
						<InputDecimal v-model="formState.dt3" :disabled="!paramsEnabled" />
					</Col>
				</Row>
			</FormItem>
			<FormItem :label="$t('settings.vrsSettingsForm.trsDScale')" v-bind="validateInfos.dscale">
				<InputDecimal v-model="formState.dscale" :disabled="!paramsEnabled" />
			</FormItem>

			<FormItem :label="$t('settings.vrsSettingsForm.trsDR')" v-bind="validateInfos.dr1">
				<Row>
					<Col :span="8">
						<InputDecimal v-model="formState.dr1" :disabled="!paramsEnabled" />
					</Col>
					<Col :span="8">
						<InputDecimal v-model="formState.dr2" :disabled="!paramsEnabled" />
					</Col>
					<Col :span="8">
						<InputDecimal v-model="formState.dr3" :disabled="!paramsEnabled" />
					</Col>
				</Row>
			</FormItem>
			<FormItem :label="$t('settings.vrsSettingsForm.trsEpoch')" v-bind="validateInfos.epochDate">
				<DatePicker v-model:value="formState.epochDate" :disabled="!paramsEnabled" />
			</FormItem>
			<FormItem :label="$t('settings.vrsSettingsForm.trsEpoch2')" v-bind="validateInfos.epoch2Date">
				<DatePicker v-model:value="formState.epoch2Date" />
			</FormItem>
			<FormItem :label="$t('settings.vrsSettingsForm.trsVelocities')" v-bind="validateInfos.velocity1">
				<Row>
					<Col :span="8">
						<InputDecimal v-model="formState.velocity1" />
					</Col>
					<Col :span="8">
						<InputDecimal v-model="formState.velocity2" />
					</Col>
					<Col :span="8">
						<InputDecimal v-model="formState.velocity3" />
					</Col>
				</Row>
			</FormItem>
			<FormButtonsItem>
				<Button type="primary" @click.prevent="onSubmit" :loading="isSubmitting">
					{{ $t('settings.vrsSettingsForm.buttonLabel') }}
				</Button>
				<Button @click.prevent="onClear" :disabled="!paramsEnabled">
					{{ $t('settings.vrsSettingsForm.clearButtonLabel') }}
				</Button>
			</FormButtonsItem>
		</Form>
	</Skeleton>
</template>

<script>
const USER_DEFINED = 'USER_DEFINED';

import { reactive, nextTick, toRaw, watch, toRef, computed } from 'vue';
import { Button, Skeleton, Form, FormItem, Row, Col, Select, DatePicker } from 'ant-design-vue';

import { useI18n } from 'vue-i18n'

import Api from '@/api/Api';
import Validators from '@/api/Validators';
import Utils from '@/api/Utils';

import { useVRSSettingsPost } from './useVRSSettingsPost.js';

import { useProvideInjectFormChangedStatuses } from '@/utils/useProvideInject';
import { usePopupContainer } from '@/utils/usePopupContainer';
import { dateStrToDayJs } from '@/components/common/dateUtils';

import InputDecimal from '@/components/common/InputDecimal';
import FormButtonsItem from '@/components/common/FormButtonsItem.vue';

export default {
	components: {
		Button, Skeleton, Form, FormItem, Row, Col, Select, DatePicker,
		InputDecimal, FormButtonsItem,
	},
	props: ['loading', 'settings', 'trsOptions'],
	setup(props) {
		const { t } = useI18n();
		const formState = reactive({
			label: '',
			t1: '',
			t2: '',
			t3: '',
			scale: '',
			r1: '',
			r2: '',
			r3: '',
			dt1: '',
			dt2: '',
			dt3: '',
			dscale: '',
			dr1: '',
			dr2: '',
			dr3: '',
			epochDate: null,
			epoch2Date: null,
			velocity1: '',
			velocity2: '',
			velocity3: '',
		});
		const trsOptionsProp = toRef(props, 'trsOptions');
		const trsOptionsRef = computed(() => trsOptionsProp.value.map(option => ({
			label: [USER_DEFINED].includes(option.label) ? t('settings.vrsSettingsForm.trsOptions.' + option.label) : option.label,
			value: option.label,
		})));
		const { validate, validateInfos, clearValidate } = Validators.useVRSSettingsTerrestrialReferenceSystemFormRules(formState);
		const { injectValue } = useProvideInjectFormChangedStatuses;
		const formChangedStatuses = injectValue();
		const { enableWatch, pauseWatch, resetChangedState } = Validators.useWatchFormChanges(formState, formChangedStatuses, 'TerrestrialReferenceSystemForm');
		const serializeFormState = formState => {
			const raw = toRaw(formState);
			raw.epoch = raw.epochDate != null ? raw.epochDate.format('YYYY-MM-DD') : null;
			raw.epoch2 = raw.epoch2Date != null ? raw.epoch2Date.format('YYYY-MM-DD') : null;
			return raw;
		};
		const { isSubmitting, onSubmit } = useVRSSettingsPost(Api.trsSettingsPost, formState, validate, serializeFormState, resetChangedState, error => {
			error.errorFields.forEach(field => {
				for (let prefix of ['t', 'r', 'dt', 'dr', 'velocity']) {	
					if ([prefix + '1', prefix + '2', prefix + '3'].includes(field.name)) {
						validateInfos[prefix + '1'].validateStatus = 'error';
						validateInfos[prefix + '1'].help = field.errors[0];
					}
				}
			});
		});
		watch(
			() => props.settings,
			value => {
				pauseWatch();
				Utils.updateObject(formState, value.terrestrialReferenceSystem);
				formState.epochDate = value.terrestrialReferenceSystem.epoch != null ? dateStrToDayJs(value.terrestrialReferenceSystem.epoch) : null;
				formState.epoch2Date = value.terrestrialReferenceSystem.epoch2 != null ? dateStrToDayJs(value.terrestrialReferenceSystem.epoch2) : null;
				nextTick(enableWatch);
			},
		);

		const onLabelChange = value => {
			clearValidate();
			if (value === USER_DEFINED) {
				return;
			}
			const options = trsOptionsProp.value.filter(option => option.label === value);
			if (options.length > 0) {
				for (let key of Object.keys(options[0])) {
					if (key === 'epoch') {
						formState.epochDate = dateStrToDayJs(options[0][key]);
					} else if (key === 'epoch2') {
						formState.epoch2Date = dateStrToDayJs(options[0][key]);
					} else if (key !== 'label') {
						formState[key] = options[0][key];
					}
				}
			}
		};
		const paramsEnabled = computed(() => {
			return [USER_DEFINED].includes(formState.label);
		});

		const onClear = () => {
			formState.t1 = '';
			formState.t2 = '';
			formState.t3 = '';
			formState.scale = '';
			formState.r1 = '';
			formState.r2 = '';
			formState.r3 = '';
			formState.dt1 = '';
			formState.dt2 = '';
			formState.dt3 = '';
			formState.dscale = '';
			formState.dr1 = '';
			formState.dr2 = '';
			formState.dr3 = '';
			formState.epochDate = null;
			formState.epoch2Date = null;
			formState.velocity1 = '';
			formState.velocity2 = '';
			formState.velocity3 = '';
		};

		const { getPopupContainer } = usePopupContainer();

		return {
			isSubmitting,
			formState,
			trsOptionsRef,
			validateInfos,
			onSubmit,
			onLabelChange,
			paramsEnabled,
			onClear,
			getPopupContainer,
		};
	},
}
</script> 